import { useEffect, useState } from "react";
import { Activity, AlertTriangle, ArrowRight, Bot, CheckCircle2, ChevronDown, Coins, Cpu, FileText, Info, ListChecks, MessageSquareText, RefreshCw, Save, Search, Users } from "lucide-react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { dictionary } from "../../../utils/dictionary";
import { decodeTokenService } from "../../../services/decodeTokenService";
import {
  getWSAIConfig,
  updateWSAIConfig,
  settleWSAIBilling,
  listAIModels,
  listFunnelsForWAConfirmation,
  setFunnelWAConfirmation,
  getWAAccountHealth,
  type TonePreset,
  type ResponseLength,
  type AIModel,
  type FunnelWithWAFlag,
  type WAAccountHealth,
  type WhatsAppConfirmationImageMode,
} from "../../../services/whatsapp/whatsappService";
import { handleApi } from "../../../utils/handleApi";
import Loader from "../../../components/common/Loader";
import TemplatesPanel from "./TemplatesPanel";
import ConfirmationFunnelCard from "./ConfirmationFunnelCard";
import ContactsListPanel from "./ContactsListPanel";
import { Autocomplete } from "../../../components/ui/Autocomplete";
import { useChatStore } from "../../../store/chatStore";

type ConfigSection = "ai" | "templates" | "funnels" | "contacts";

const WS_SECTION_PARAM = "ws_section";
const isValidSection = (v: string | null): v is ConfigSection =>
  v === "ai" || v === "templates" || v === "funnels" || v === "contacts";

const TONE_OPTIONS: { id: TonePreset; icon: string }[] = [
  { id: "professional", icon: "👔" },
  { id: "friendly", icon: "😊" },
  { id: "casual", icon: "🤙" },
  { id: "custom", icon: "✏️" },
];

const LENGTH_OPTIONS: ResponseLength[] = ["short", "medium", "long"];

const WSConfigTab = () => {
  const { language } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [qualityTooltipOpen, setQualityTooltipOpen] = useState(false);
  const { whatsappAccount } = useChatStore();

  // Persist the active sub-tab in the URL (?ws_section=templates) so a
  // refresh keeps the user on the tab they were viewing.
  const [section, setSectionState] = useState<ConfigSection>(() => {
    const s = searchParams.get(WS_SECTION_PARAM);
    return isValidSection(s) ? s : "ai";
  });

  const setSection = (next: ConfigSection) => {
    setSectionState(next);
    const params = new URLSearchParams(searchParams);
    params.set(WS_SECTION_PARAM, next);
    setSearchParams(params, { replace: true });
  };

  // If someone lands without the param (first visit), drop it in so a
  // later refresh already knows where to return.
  useEffect(() => {
    if (!searchParams.get(WS_SECTION_PARAM)) {
      const params = new URLSearchParams(searchParams);
      params.set(WS_SECTION_PARAM, section);
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tonePreset, setTonePreset] = useState<TonePreset>("friendly");
  const [customInstructions, setCustomInstructions] = useState("");
  const [maxResponseLength, setMaxResponseLength] = useState<ResponseLength>("medium");
  const [modelProvider, setModelProvider] = useState<string>("google");
  const [modelName, setModelName] = useState<string>("gemini-3.5-flash");
  const [availableModels, setAvailableModels] = useState<AIModel[]>([]);
  const [funnels, setFunnels] = useState<FunnelWithWAFlag[]>([]);
  const [funnelsLoading, setFunnelsLoading] = useState(false);
  const [health, setHealth] = useState<WAAccountHealth | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [settlingBilling, setSettlingBilling] = useState(false);
  const [billingStatus, setBillingStatus] = useState<{
    pending_credits: number;
    delinquency_limit: number;
    is_delinquent: boolean;
  } | null>(null);
  const [promptPreview, setPromptPreview] = useState("");
  const [promptPreviewOpen, setPromptPreviewOpen] = useState(false);

  useEffect(() => {
    loadConfig();
    loadFunnels();
    loadHealth();
  }, []);

  // Re-fetch model estimates whenever the response length changes so the
  // displayed cost reflects the current setting.
  useEffect(() => {
    loadModels(maxResponseLength);
  }, [maxResponseLength]);

  const loadHealth = async () => {
    const ownerId = decodeTokenService.getUserIdFromToken();
    if (!ownerId) {
      setHealthLoading(false);
      return;
    }
    setHealthLoading(true);
    const { res } = await handleApi(getWAAccountHealth(ownerId), false);
    if (res) setHealth(res);
    setHealthLoading(false);
  };

  const qualityColor = (rating: string | null | undefined) => {
    if (rating === "GREEN") return "bg-green-100 text-green-700 border-green-200";
    if (rating === "YELLOW") return "bg-yellow-100 text-yellow-700 border-yellow-200";
    if (rating === "RED") return "bg-red-100 text-red-700 border-red-200";
    return "bg-gray-100 text-gray-600 border-gray-200";
  };

  const qualityLabel = (rating: string | null | undefined) => {
    if (rating === "GREEN") return dictionary["ws-config-quality-green"]?.[language] ?? "Buena calidad";
    if (rating === "YELLOW") return dictionary["ws-config-quality-yellow"]?.[language] ?? "Calidad media";
    if (rating === "RED") return dictionary["ws-config-quality-red"]?.[language] ?? "Calidad baja — riesgo";
    return dictionary["ws-config-quality-unknown"]?.[language] ?? "Sin información aún";
  };

  const loadFunnels = async () => {
    setFunnelsLoading(true);
    const { res } = await handleApi(listFunnelsForWAConfirmation(), false);
    if (res) setFunnels(res);
    setFunnelsLoading(false);
  };

  const toggleFunnelEnabled = async (funnel: FunnelWithWAFlag) => {
    const nextEnabled = !funnel.whatsapp_confirmation_enabled;
    setFunnels((curr) =>
      curr.map((f) =>
        f.id === funnel.id ? { ...f, whatsapp_confirmation_enabled: nextEnabled } : f
      )
    );
    try {
      await setFunnelWAConfirmation(
        funnel.id,
        nextEnabled,
        (funnel.configurations as Record<string, unknown>) || {}
      );
    } catch (err: unknown) {
      setFunnels((curr) =>
        curr.map((f) =>
          f.id === funnel.id ? { ...f, whatsapp_confirmation_enabled: !nextEnabled } : f
        )
      );
      const msg =
        (err as { response?: { data?: { message?: string } }; message?: string })
          ?.response?.data?.message ??
        (err as { message?: string })?.message ??
        (dictionary["ws-config-funnels-save-error"]?.[language] ??
          "No se pudo guardar el embudo");
      toast.error(msg);
    }
  };

  const updateFunnelImageConfig = async (
    funnel: FunnelWithWAFlag,
    imageMode: WhatsAppConfirmationImageMode,
    imageUrl: string | null = null
  ) => {
    const previous = funnels;
    setFunnels((curr) =>
      curr.map((f) =>
        f.id === funnel.id
          ? {
              ...f,
              whatsapp_confirmation_image_mode: imageMode,
              whatsapp_confirmation_image_url: imageUrl,
              configurations: {
                ...(f.configurations || {}),
                whatsapp_confirmation_image_mode: imageMode,
                whatsapp_confirmation_image_url: imageUrl,
              },
            }
          : f
      )
    );
    try {
      await setFunnelWAConfirmation(
        funnel.id,
        true,
        (funnel.configurations as Record<string, unknown>) || {},
        { imageMode, imageUrl }
      );
      toast.success(
        dictionary["ws-config-funnels-image-saved"]?.[language] ??
          "Imagen de confirmación guardada"
      );
    } catch (err: unknown) {
      setFunnels(previous);
      const msg =
        (err as { response?: { data?: { message?: string } }; message?: string })
          ?.response?.data?.message ??
        (err as { message?: string })?.message ??
        (dictionary["ws-config-funnels-save-error"]?.[language] ??
          "No se pudo guardar el embudo");
      toast.error(msg);
    }
  };

  const loadConfig = async () => {
    const ownerId = decodeTokenService.getUserIdFromToken();
    if (!ownerId) return;

    setLoading(true);
    const { res } = await handleApi(getWSAIConfig(ownerId), false);
    if (res) {
      setTonePreset(res.tone_preset);
      setCustomInstructions(res.custom_instructions || "");
      setMaxResponseLength(res.max_response_length);
      setModelProvider(res.model_provider || "google");
      setModelName(res.model_name || "gemini-3.5-flash");
      setBillingStatus(res.billing_status ?? null);
      setPromptPreview(res.prompt_preview || "");
    }
    setLoading(false);
  };

  const loadModels = async (responseLength: ResponseLength) => {
    const { res } = await handleApi(listAIModels(responseLength), false);
    if (res) setAvailableModels(res);
  };

  const selectedModel = availableModels.find(
    (m) => m.provider === modelProvider && m.model === modelName
  );

  const handleSave = async () => {
    const ownerId = decodeTokenService.getUserIdFromToken();
    if (!ownerId) return;

    setSaving(true);
    const { res, error } = await handleApi(
      updateWSAIConfig(ownerId, {
        tone_preset: tonePreset,
        custom_instructions: customInstructions,
        max_response_length: maxResponseLength,
        model_provider: modelProvider,
        model_name: modelName,
      }),
      false
    );

    if (res) {
      setBillingStatus(res.billing_status ?? billingStatus);
      setPromptPreview(res.prompt_preview || promptPreview);
      toast.success(dictionary["ws-config-saved"]?.[language] ?? "Configuración guardada");
    } else if (error) {
      toast.error(dictionary["ws-config-save-error"]?.[language] ?? "Error al guardar");
    }
    setSaving(false);
  };

  const handleSettleBilling = async () => {
    const ownerId = decodeTokenService.getUserIdFromToken();
    if (!ownerId) return;

    setSettlingBilling(true);
    const { res, error } = await handleApi(settleWSAIBilling(ownerId), false);
    if (res) {
      setBillingStatus(res.billing_status);
      if (res.billing_status.is_delinquent) {
        toast.error(
          dictionary["ws-config-ai-settle-still-delinquent"]?.[language] ??
            "No se pudo saldar la mora. Revisa que tengas Fluxi Coins disponibles."
        );
      } else {
        toast.success(
          dictionary["ws-config-ai-settle-success"]?.[language] ??
            "Saldo pendiente descontado correctamente"
        );
      }
    } else if (error) {
      toast.error(
        dictionary["ws-config-ai-settle-error"]?.[language] ??
          "No se pudo descontar el saldo pendiente"
      );
    }
    setSettlingBilling(false);
  };

  if (loading && section === "ai") {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader />
      </div>
    );
  }

  const tabs: { id: ConfigSection; labelKey: string; fallback: string; Icon: typeof Bot }[] = [
    { id: "ai", labelKey: "ws-config-tab-ai", fallback: "Asistente IA", Icon: Bot },
    { id: "funnels", labelKey: "ws-config-tab-funnels", fallback: "Embudos", Icon: ListChecks },
    { id: "templates", labelKey: "ws-config-tab-templates", fallback: "Plantillas", Icon: FileText },
    { id: "contacts", labelKey: "ws-config-tab-contacts", fallback: "Contactos", Icon: Users },
  ];

  const renderTokenChip = () => {
    if (!health) return null;
    const h = health;
    const isExpired = h.token_status === "expired";
    const isUnknown = h.token_status === "unknown" && h.token_seconds_remaining === null;
    if (isUnknown) return null;

    const expiresLabel = (() => {
      if (isExpired) {
        return dictionary["ws-config-token-expired"]?.[language] ?? "Token expirado";
      }
      const days = h.token_days_remaining ?? 0;
      const hours = h.token_hours_remaining ?? 0;
      const minutes = h.token_minutes_remaining ?? 0;
      const prefix = dictionary["ws-config-token-expires-in"]?.[language] ?? "Token expira en";
      if (days > 0) {
        return `${prefix} ${days} ${dictionary["ws-config-token-days"]?.[language] ?? "días"}`;
      }
      if (hours > 0) {
        return `${prefix} ${hours}h ${minutes}min`;
      }
      if (minutes > 0) {
        return `${prefix} ${minutes}min`;
      }
      return dictionary["ws-config-token-expired"]?.[language] ?? "Token expirado";
    })();

    const danger = isExpired || h.token_expiring_soon;
    return (
      <div
        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${
          danger
            ? "bg-red-50 text-red-700 border-red-200"
            : "bg-gray-100 text-gray-600 border-gray-200"
        }`}
      >
        {danger ? (
          <AlertTriangle className="w-3.5 h-3.5" />
        ) : (
          <RefreshCw className="w-3.5 h-3.5" />
        )}
        <span>{expiresLabel}</span>
      </div>
    );
  };

  // Connection status mirrors the integration's is_active flag — the token
  // expiration is shown as its own chip so the owner can act on it without
  // confusing it with a "disconnected" state.
  const apiConnected = !!whatsappAccount?.is_active;

  const qualityTooltipText = (() => {
    const rating = health?.quality_rating;
    if (rating === "GREEN") {
      return dictionary["ws-config-quality-green-tooltip"]?.[language] ??
        "Tu cuenta tiene buena reputación con Meta. Pocos clientes han bloqueado o reportado tus mensajes, así que puedes seguir enviando con normalidad.";
    }
    if (rating === "YELLOW") {
      return dictionary["ws-config-quality-yellow-tooltip"]?.[language] ??
        "Algunos clientes están bloqueando o reportando tus mensajes. Si baja más, Meta puede limitar tu envío. Revisa el contenido de tus templates.";
    }
    if (rating === "RED") {
      return dictionary["ws-config-quality-red-tooltip"]?.[language] ??
        "Muchos clientes están reportando tus mensajes. Meta podría suspender tu cuenta. Revisa urgentemente qué estás enviando y a quién.";
    }
    return dictionary["ws-config-quality-unknown-tooltip"]?.[language] ??
      "Aún no hay suficientes mensajes enviados para que Meta evalúe la calidad de tu cuenta.";
  })();

  const metaApiBox = (
    <div className="rounded-2xl border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">
            {dictionary["ws-config-meta-api-title"]?.[language] ?? "Estado de la API de Meta"}
          </h3>
          <div
            className="relative inline-flex"
            onMouseEnter={() => setTooltipOpen(true)}
            onMouseLeave={() => setTooltipOpen(false)}
            onFocus={() => setTooltipOpen(true)}
            onBlur={() => setTooltipOpen(false)}
          >
            <Info
              className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help"
              tabIndex={0}
              aria-label={dictionary["ws-config-meta-api-tooltip"]?.[language] ?? ""}
            />
            {tooltipOpen && (
              <div className="absolute z-20 left-1/2 -translate-x-1/2 top-full mt-2 w-64 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg leading-snug">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" />
                {dictionary["ws-config-meta-api-tooltip"]?.[language] ??
                  "Toda esta información se obtiene directamente de la API de Meta y se actualiza automáticamente."}
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/integrations?reconnect=whatsapp")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          {dictionary["ws-config-manage-api"]?.[language] ?? "Gestionar API"}
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {/* API connected indicator */}
        {healthLoading ? (
          <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium bg-gray-50 text-gray-400 border-gray-200">
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            <span className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
          </div>
        ) : (
          <div
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${
              apiConnected
                ? "bg-green-50 text-green-700 border-green-200"
                : "bg-red-50 text-red-700 border-red-200"
            }`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>
              {apiConnected
                ? dictionary["ws-config-api-connected"]?.[language] ?? "API conectada"
                : dictionary["ws-config-api-disconnected"]?.[language] ?? "API desconectada"}
            </span>
          </div>
        )}

        {/* Quality rating chip — clickable tooltip explains what the rating means */}
        {!healthLoading && health && (
          <div
            className="relative"
            onMouseEnter={() => setQualityTooltipOpen(true)}
            onMouseLeave={() => setQualityTooltipOpen(false)}
            onFocus={() => setQualityTooltipOpen(true)}
            onBlur={() => setQualityTooltipOpen(false)}
          >
            <div
              tabIndex={0}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium cursor-help ${qualityColor(
                health.quality_rating
              )}`}
            >
              <Activity className="w-3.5 h-3.5" />
              <span>{qualityLabel(health.quality_rating)}</span>
              {health.messaging_limit_tier && (
                <span className="opacity-70">· {health.messaging_limit_tier}</span>
              )}
            </div>
            {qualityTooltipOpen && (
              <div className="absolute z-30 left-0 top-full mt-2 w-72 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg leading-snug">
                <div className="absolute top-0 left-6 -translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" />
                {qualityTooltipText}
              </div>
            )}
          </div>
        )}

        {/* Token expiration chip */}
        {!healthLoading && renderTokenChip()}
      </div>
    </div>
  );

  return (
    <div className="h-full overflow-y-auto">
      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-8">
          <nav className="flex gap-1">
            {tabs.map(({ id, labelKey, fallback, Icon }) => (
              <button
                key={id}
                onClick={() => setSection(id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  section === id
                    ? "border-gray-900 text-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Icon className="w-4 h-4" />
                {dictionary[labelKey]?.[language] ?? fallback}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {section === "templates" && <TemplatesPanel />}
      {section === "contacts" && <ContactsListPanel />}

      {section === "ai" && (
      <div className="max-w-5xl mx-auto p-8 font-['Plus_Jakarta_Sans']">
        {/* Meta API status box — placed at the top of the assistant settings */}
        <div className="mb-8">{metaApiBox}</div>

        {billingStatus?.is_delinquent && (
          <div className="mb-8 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-red-800">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold">
                  {dictionary["ws-config-ai-delinquent-title"]?.[language] ??
                    "Asistente en mora"}
                </h2>
                <p className="text-sm mt-1 leading-relaxed">
                  {(
                    dictionary["ws-config-ai-delinquent-desc"]?.[language] ??
                    "Tienes {pending} Fluxi Coins pendientes por mensajes enviados. Al superar {limit} coins, la IA deja de responder automáticamente hasta que recargues o se descuente el saldo pendiente."
                  )
                    .replace("{pending}", billingStatus.pending_credits.toFixed(2))
                    .replace("{limit}", String(billingStatus.delinquency_limit))}
                </p>
              </div>
              <button
                type="button"
                onClick={handleSettleBilling}
                disabled={settlingBilling}
                className="ml-auto inline-flex flex-shrink-0 items-center justify-center rounded-lg bg-red-700 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {settlingBilling
                  ? dictionary["ws-config-ai-settle-loading"]?.[language] ?? "Descontando..."
                  : dictionary["ws-config-ai-settle-button"]?.[language] ?? "Pagar saldo"}
              </button>
            </div>
          </div>
        )}

        {/* Header + Save */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {dictionary["ws-config-title"]?.[language] ?? "Configuración del Asistente WhatsApp"}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {dictionary["ws-config-subtitle"]?.[language] ?? "Personaliza cómo se comporta la IA al confirmar pedidos"}
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 bg-gray-900 text-white rounded-xl font-medium text-sm hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 flex-shrink-0"
          >
            <Save className="w-4 h-4" />
            {saving
              ? (dictionary["ws-config-saving"]?.[language] ?? "Guardando...")
              : (dictionary["ws-config-save"]?.[language] ?? "Guardar")
            }
          </button>
        </div>

        <section className="mb-8 rounded-2xl border border-gray-200 bg-white">
          <button
            type="button"
            onClick={() => setPromptPreviewOpen((prev) => !prev)}
            className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
          >
            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                {dictionary["ws-config-prompt-preview-title"]?.[language] ?? "Prompt que recibe la IA"}
              </h2>
              <p className="mt-1 text-xs text-gray-500">
                {dictionary["ws-config-prompt-preview-desc"]?.[language] ??
                  "Vista de lectura con datos de ejemplo; cambia cuando guardas tono, largo o instrucciones."}
              </p>
            </div>
            <ChevronDown
              className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform ${
                promptPreviewOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {promptPreviewOpen && (
            <div className="border-t border-gray-100 px-5 py-4">
              <pre className="max-h-[420px] overflow-auto whitespace-pre-wrap rounded-xl bg-gray-950 p-4 text-xs leading-relaxed text-gray-100">
                {promptPreview || "Prompt no disponible todavía."}
              </pre>
            </div>
          )}
        </section>

        {/* Two-column layout */}
        <div className="grid grid-cols-2 gap-8">
          {/* Left column: Tone + Length */}
          <div className="space-y-6">
            {/* Tone Selection */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Bot className="w-5 h-5 text-gray-700" />
                <h2 className="text-base font-semibold text-gray-900">
                  {dictionary["ws-config-tone-title"]?.[language] ?? "Tono de la IA"}
                </h2>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {TONE_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setTonePreset(option.id)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      tonePreset === option.id
                        ? "border-gray-900 bg-gray-50"
                        : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}
                  >
                    <div className="text-xl mb-1">{option.icon}</div>
                    <div className="font-medium text-sm text-gray-900">
                      {dictionary[`ws-config-tone-${option.id}`]?.[language] ?? option.id}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {dictionary[`ws-config-tone-${option.id}-desc`]?.[language] ?? ""}
                    </div>
                  </button>
                ))}
              </div>
            </section>

            {/* Response Length */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <MessageSquareText className="w-5 h-5 text-gray-700" />
                <h2 className="text-base font-semibold text-gray-900">
                  {dictionary["ws-config-length-title"]?.[language] ?? "Largo de respuesta"}
                </h2>
              </div>
              <div className="flex gap-3">
                {LENGTH_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setMaxResponseLength(opt)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${
                      maxResponseLength === opt
                        ? "border-gray-900 bg-gray-50 text-gray-900"
                        : "border-gray-200 text-gray-500 hover:border-gray-300 bg-white"
                    }`}
                  >
                    {dictionary[`ws-config-length-${opt}`]?.[language] ?? opt}
                  </button>
                ))}
              </div>
            </section>
          </div>

          {/* Right column: Model + Custom Instructions */}
          <div className="space-y-6">
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Cpu className="w-5 h-5 text-gray-700" />
                <h2 className="text-base font-semibold text-gray-900">
                  {dictionary["ws-config-model-title"]?.[language] ?? "Modelo de IA"}
                </h2>
              </div>
              <select
                value={`${modelProvider}::${modelName}`}
                onChange={(e) => {
                  const [p, m] = e.target.value.split("::");
                  setModelProvider(p);
                  setModelName(m);
                }}
                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                disabled={availableModels.length === 0}
              >
                {availableModels.length === 0 && (
                  <option value={`${modelProvider}::${modelName}`}>
                    {modelName}
                  </option>
                )}
                {availableModels.map((m) => (
                  <option key={`${m.provider}::${m.model}`} value={`${m.provider}::${m.model}`}>
                    {m.display_name}
                  </option>
                ))}
              </select>
              {selectedModel && (
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                  <Coins className="w-3.5 h-3.5 text-gray-500" />
                  <span>
                    {dictionary["ws-config-model-estimate"]?.[language] ?? "Estimado"}:
                    <strong className="ml-1">
                      {selectedModel.estimated_credits_per_message.toFixed(2)}
                    </strong>{" "}
                    {dictionary["ws-config-model-credits-per-message"]?.[language] ?? "créditos por mensaje"}
                  </span>
                </div>
              )}
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <MessageSquareText className="w-5 h-5 text-gray-700" />
                <h2 className="text-base font-semibold text-gray-900">
                  {dictionary["ws-config-instructions-title"]?.[language] ?? "Instrucciones personalizadas"}
                </h2>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                {dictionary["ws-config-instructions-desc"]?.[language] ??
                  "Agrega instrucciones específicas para tu negocio. Ej: \"Siempre menciona nuestra política de devoluciones\""}
              </p>
              <textarea
                value={customInstructions}
                onChange={(e) => {
                  if (e.target.value.length <= 500) setCustomInstructions(e.target.value);
                }}
                placeholder={dictionary["ws-config-instructions-placeholder"]?.[language] ?? "Escribe instrucciones adicionales para la IA..."}
                className="w-full h-56 px-4 py-3 text-sm border border-gray-200 rounded-xl bg-white resize-none focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
              <div className="text-xs text-gray-400 text-right mt-1">
                {customInstructions.length}/500
              </div>
            </section>
          </div>
        </div>
      </div>
      )}

      {section === "funnels" && (
        <div className="max-w-5xl mx-auto p-8 font-['Plus_Jakarta_Sans']">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">
              {dictionary["ws-config-funnels-title"]?.[language] ?? "Embudos afiliados a confirmación"}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {dictionary["ws-config-funnels-desc"]?.[language] ??
                "Busca un embudo y afílialo. Solo los embudos conectados aparecen abajo."}
            </p>
          </div>

          <div className="mb-8">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Search className="w-4 h-4" />
              {dictionary["ws-config-funnels-search-label"]?.[language] ?? "Agregar embudo"}
            </label>
            <Autocomplete
              value={null}
              onChange={(value) => {
                if (!value) return;
                const target = funnels.find((f) => String(f.id) === value);
                if (target && !target.whatsapp_confirmation_enabled) {
                  toggleFunnelEnabled(target);
                }
              }}
              options={funnels
                .filter((f) => !f.whatsapp_confirmation_enabled)
                .map((f) => ({
                  value: String(f.id),
                  label: f.configurations?.name_funnel || f.name,
                }))}
              placeholder={
                dictionary["ws-config-funnels-placeholder"]?.[language] ??
                "Buscar embudo por nombre..."
              }
              emptyMessage={
                dictionary["ws-config-funnels-search-empty"]?.[language] ??
                "Todos los embudos ya están afiliados"
              }
            />
          </div>

          {funnelsLoading ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (() => {
            const enabled = funnels.filter((f) => f.whatsapp_confirmation_enabled);
            if (enabled.length === 0) {
              return (
                <div className="text-center text-sm text-gray-400 py-12 border border-dashed border-gray-200 rounded-2xl">
                  {dictionary["ws-config-funnels-none-connected"]?.[language] ??
                    "Aún no has afiliado ningún embudo. Búscalo arriba para empezar."}
                </div>
              );
            }
            return (
              <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {enabled.map((f) => (
                  <ConfirmationFunnelCard
                    key={f.id}
                    funnel={f}
                    onRemove={() => toggleFunnelEnabled(f)}
                    onImageConfigChange={(mode, imageUrl) => updateFunnelImageConfig(f, mode, imageUrl)}
                  />
                ))}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default WSConfigTab;
